import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { customInstance } from '../../services/api-client'
import type { CloseCalendarItem, CloseCalendarPeriod, CloseCalendarRule, CloseCalendarTemplate } from './types'

const BASE = '/api/close-calendar'

const keys = {
  all: ['close-calendar'] as const,
  current: (closePeriod?: string) => [...keys.all, 'current', closePeriod ?? 'default'] as const,
  template: () => [...keys.all, 'template'] as const,
} as const

export function useCloseCalendarCurrentPeriod(closePeriod?: string) {
  return useQuery<CloseCalendarPeriod>({
    queryKey: keys.current(closePeriod),
    queryFn: () =>
      customInstance<CloseCalendarPeriod>({
        url: closePeriod
          ? `${BASE}/periods/current?close_period=${encodeURIComponent(closePeriod)}`
          : `${BASE}/periods/current`,
      }),
    retry: false,
    staleTime: 30_000,
  })
}

export function useCloseCalendarActiveTemplate() {
  return useQuery<CloseCalendarTemplate>({
    queryKey: keys.template(),
    queryFn: () =>
      customInstance<CloseCalendarTemplate>({
        url: `${BASE}/templates/active`,
      }),
    retry: false,
    staleTime: 60_000,
  })
}

export function useApproveCloseCalendarItem(closePeriod?: string) {
  const queryClient = useQueryClient()
  return useMutation<CloseCalendarItem, Error, number>({
    mutationFn: itemId =>
      customInstance<CloseCalendarItem>({
        url: `${BASE}/items/${itemId}/approve`,
        method: 'POST',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.current(closePeriod) })
      queryClient.invalidateQueries({ queryKey: keys.template() })
    },
  })
}

export type ConditionItem =
  | { type: 'applet'; key: string }
  | { type: 'approval'; key: string }

export type RuleCreatePayload = {
  templateId: number
  target_item_key: string
  conditions: ConditionItem[]
  name?: string
  guarded_action?: string
  enforcement_mode?: 'hard' | 'soft'
  condition_logic?: 'all' | 'any'
}

export function useCreateCloseCalendarRule() {
  const queryClient = useQueryClient()
  return useMutation<CloseCalendarRule, Error, RuleCreatePayload>({
    mutationFn: ({ templateId, ...body }) =>
      customInstance<CloseCalendarRule>({
        url: `${BASE}/templates/${templateId}/rules`,
        method: 'POST',
        data: body,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.all })
    },
  })
}

export type RuleUpdatePayload = {
  ruleId: number
  enforcement_mode?: 'hard' | 'soft'
  condition_logic?: 'all' | 'any'
  is_active?: boolean
  allow_override?: boolean
  override_requires_reason?: boolean
  override_requires_dual_approval?: boolean
}

export function useUpdateCloseCalendarRule() {
  const queryClient = useQueryClient()
  return useMutation<CloseCalendarRule, Error, RuleUpdatePayload>({
    mutationFn: ({ ruleId, ...body }) =>
      customInstance<CloseCalendarRule>({
        url: `${BASE}/rules/${ruleId}`,
        method: 'PATCH',
        data: body,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.all })
    },
  })
}

export function useDeleteCloseCalendarRule() {
  const queryClient = useQueryClient()
  return useMutation<void, Error, number>({
    mutationFn: ruleId =>
      customInstance<void>({
        url: `${BASE}/rules/${ruleId}`,
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.all })
    },
  })
}
